import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import {
  Plus,
  Trash2,
  ExternalLink,
  Key,
  DollarSign,
  Plug,
  Copy,
  ChevronDown,
} from "lucide-react";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../components/ui/popover";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import ExportImport from "../components/ExportImport";

const STATUSES = [
  { value: "Paid",       bg: "bg-mint-100",   text: "text-emerald-700" },
  { value: "Trial",        bg: "bg-butter-100", text: "text-amber-700" },
  { value: "Free Version", bg: "bg-sky-100",    text: "text-sky-700" },
  { value: "Custom",       bg: "bg-lilac-100",  text: "text-purple-700" },
];
const statusMeta = (v) => STATUSES.find((s) => s.value === v) || STATUSES[2];

// Auto-growing textarea for free-text cells. Looks like an input but wraps to multiple lines.
function WrapInput({ value, onCommit, placeholder = "—", testid, className = "" }) {
  const ref = React.useRef(null);
  const resize = () => {
    const el = ref.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  };
  React.useEffect(() => { resize(); }, [value]);
  return (
    <textarea
      ref={ref}
      cols={1}
      defaultValue={value || ""}
      onInput={resize}
      onBlur={(e) => {
        const v = e.target.value;
        if (v !== (value || "")) onCommit(v);
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); e.currentTarget.blur(); }
      }}
      placeholder={placeholder}
      data-testid={testid}
      rows={1}
      className={`block w-full min-w-0 bg-transparent text-sm text-slate-700 outline-none focus:bg-white focus:rounded-md focus:border focus:border-slate-200 placeholder:text-slate-400 resize-none leading-snug py-1 px-1 whitespace-pre-wrap break-words ${className}`}
    />
  );
}

const PAYING_ENTITIES = ["Wio", "ENBD", "Kotak", "Gaup", "Others"];

const GRID = "120px 1.1fr 2fr 1fr 130px 1fr 170px";

// Palette for type chips — stable per-option color based on hash of label
const TYPE_PALETTE = [
  { bg: "bg-sky-100",    text: "text-sky-800",    border: "border-sky-200" },
  { bg: "bg-mint-100",   text: "text-emerald-800", border: "border-mint-200" },
  { bg: "bg-butter-100", text: "text-amber-800",   border: "border-butter-200" },
  { bg: "bg-peach-100",  text: "text-amber-900",   border: "border-peach-200" },
  { bg: "bg-lilac-100",  text: "text-purple-800",  border: "border-lilac-200" },
  { bg: "bg-rose-100",   text: "text-rose-800",    border: "border-rose-200" },
];
function typeMeta(label) {
  if (!label) return null;
  let h = 0;
  for (let i = 0; i < label.length; i += 1) h = (h * 31 + label.charCodeAt(i)) | 0;
  return TYPE_PALETTE[Math.abs(h) % TYPE_PALETTE.length];
}

function TypeSelect({ value, options, onChange, onOptionsChange, testid }) {
  const [open, setOpen] = useState(false);
  const [addingNew, setAddingNew] = useState(false);
  const [draft, setDraft] = useState("");

  const meta = typeMeta(value);
  const addNew = () => {
    const v = (draft || "").trim();
    if (!v) return;
    if (!options.includes(v)) {
      onOptionsChange([...options, v]);
    }
    onChange(v);
    setDraft("");
    setAddingNew(false);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={testid}
          className={`h-8 px-2.5 rounded-md text-xs font-medium inline-flex items-center gap-1 border transition-all min-w-0 max-w-full ${
            value && meta
              ? `${meta.bg} ${meta.text} ${meta.border}`
              : "bg-slate-50 text-slate-400 border-slate-200 border-dashed hover:border-slate-300"
          }`}
        >
          <span className="truncate">{value || "Pick type"}</span>
          <ChevronDown className="h-3 w-3 shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-3" align="start">
        <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-slate-500 mb-2">Type</p>
        <div className="flex flex-wrap gap-1.5">
          {options.length === 0 && (
            <p className="text-xs text-slate-400">No options yet — add one below.</p>
          )}
          {options.map((opt) => {
            const m = typeMeta(opt);
            const active = opt === value;
            return (
              <button
                key={opt}
                type="button"
                onClick={() => { onChange(opt); setOpen(false); }}
                className={`group relative h-7 px-2.5 rounded-md text-xs font-medium flex items-center gap-1 border transition-all ${m.bg} ${m.text} ${m.border} ${
                  active ? "ring-2 ring-offset-1 ring-slate-400" : "hover:brightness-95"
                }`}
                data-testid={`type-opt-${opt}`}
              >
                {opt}
                <span
                  role="button"
                  tabIndex={-1}
                  onClick={(e) => {
                    e.stopPropagation();
                    const next = options.filter((x) => x !== opt);
                    onOptionsChange(next);
                    if (value === opt) onChange("");
                  }}
                  className="opacity-0 group-hover:opacity-100 h-4 w-4 rounded-full hover:bg-white/50 flex items-center justify-center transition-all"
                  title={`Remove "${opt}"`}
                >
                  <Trash2 className="h-3 w-3" />
                </span>
              </button>
            );
          })}
        </div>
        <div className="h-px bg-slate-100 my-3" />
        {addingNew ? (
          <div className="flex items-center gap-2">
            <Input
              autoFocus
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") { e.preventDefault(); addNew(); }
                if (e.key === "Escape") { setDraft(""); setAddingNew(false); }
              }}
              placeholder="New type name"
              className="h-8 text-sm"
              data-testid="type-new-input"
            />
            <Button size="sm" onClick={addNew} className="btn-brand h-8">Add</Button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setAddingNew(true)}
            className="w-full h-8 rounded-md border border-dashed border-slate-200 text-xs font-medium text-slate-500 hover:text-slate-800 hover:border-slate-300 hover:bg-slate-50 flex items-center justify-center gap-1 transition-all"
            data-testid="type-add-option"
          >
            <Plus className="h-3.5 w-3.5" /> Add option
          </button>
        )}
        {value && (
          <button
            type="button"
            onClick={() => { onChange(""); setOpen(false); }}
            className="mt-2 text-[11px] text-slate-400 hover:text-slate-700"
          >
            Clear selection
          </button>
        )}
      </PopoverContent>
    </Popover>
  );
}

function UrlPopover({ value, onSave, testid }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(value || "");
  useEffect(() => { setDraft(value || ""); }, [value]);

  const commit = () => {
    if (draft !== (value || "")) onSave(draft);
    setOpen(false);
  };

  const click = (e) => {
    e.stopPropagation();
    if (value) window.open(value, "_blank", "noopener,noreferrer");
    else setOpen(true);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          onClick={click}
          onContextMenu={(e) => { e.preventDefault(); setOpen(true); }}
          className={`h-7 w-7 rounded-md flex items-center justify-center transition-all shrink-0 ${
            value ? "text-blue-600 hover:bg-sky-50" : "text-slate-300 hover:text-slate-500 hover:bg-slate-100"
          }`}
          data-testid={testid}
          title={value ? `Open ${value} (right-click to edit)` : "Add URL"}
        >
          <ExternalLink className="h-3.5 w-3.5" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-3" align="start">
        <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-slate-500 mb-2">API URL</p>
        <div className="flex gap-2">
          <Input
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") commit(); if (e.key === "Escape") setOpen(false); }}
            placeholder="https://…"
            className="h-8 text-sm"
          />
          <Button size="sm" onClick={commit} className="btn-brand h-8">Save</Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function KeyDialog({ open, onOpenChange, api: row, onSave }) {
  const [form, setForm] = useState({ login_url: "", email: "", password: "", paying_entity: "" });

  useEffect(() => {
    if (open && row) setForm({
      login_url: row.key?.login_url || "",
      email: row.key?.email || "",
      password: row.key?.password || "",
      paying_entity: row.key?.paying_entity || "",
    });
  }, [open, row]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    try {
      await onSave({ key: form });
      onOpenChange(false);
      toast.success("Key saved");
    } catch { toast.error("Failed to save"); }
  };

  if (!row) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="api-key-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Key for {row.name}</DialogTitle>
          <DialogDescription>Store login credentials and paying entity for this API.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label className="text-xs font-bold uppercase tracking-[0.1em] text-slate-500">Login URL</Label>
            <Input value={form.login_url} onChange={(e) => update("login_url", e.target.value)} placeholder="https://provider.com/login" className="mt-1" data-testid="api-key-login-url" />
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-[0.1em] text-slate-500">Email</Label>
            <Input type="email" value={form.email} onChange={(e) => update("email", e.target.value)} placeholder="billing@company.com" className="mt-1" data-testid="api-key-email" />
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-[0.1em] text-slate-500">Password</Label>
            <Input type="password" value={form.password} onChange={(e) => update("password", e.target.value)} placeholder="••••••••" className="mt-1" data-testid="api-key-password" />
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-[0.1em] text-slate-500">Paying Entity</Label>
            <Select value={form.paying_entity} onValueChange={(v) => update("paying_entity", v)}>
              <SelectTrigger className="mt-1" data-testid="api-key-entity"><SelectValue placeholder="Select…" /></SelectTrigger>
              <SelectContent>
                {PAYING_ENTITIES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} className="btn-brand" data-testid="api-key-save">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PackageCostDialog({ open, onOpenChange, api: row, onSave }) {
  const [items, setItems] = useState([]);
  const CURRENCIES = ["USD", "INR", "EUR", "GBP", "AUD", "CAD", "JPY", "CNY", "SGD", "AED"];

  useEffect(() => {
    if (open && row) setItems((row.package_costs || []).map((p) => ({ currency: "USD", ...p })));
  }, [open, row]);

  const addRow = () => setItems((prev) => [...prev, { id: `tmp-${Date.now()}-${Math.random().toString(36).slice(-5)}`, token_details: "", range: "", currency: "USD", price: "" }]);
  const updateRow = (id, k, v) => setItems((prev) => prev.map((it) => (it.id === id ? { ...it, [k]: v } : it)));
  const removeRow = (id) => setItems((prev) => prev.filter((it) => it.id !== id));

  const submit = async () => {
    try {
      await onSave({ package_costs: items.map(({ id, token_details, range, currency, price }) => ({ id, token_details, range, currency: currency || "USD", price })) });
      onOpenChange(false);
      toast.success("Package cost saved");
    } catch { toast.error("Failed to save"); }
  };

  if (!row) return null;
  const COLS = "1.3fr 1fr 90px 1fr 40px";
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl" data-testid="api-cost-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Package cost for {row.name}</DialogTitle>
          <DialogDescription>Track token tiers, ranges and prices.</DialogDescription>
        </DialogHeader>
        <div className="rounded-xl border border-slate-200 overflow-hidden">
          <div className="grid gap-2 text-[11px] font-bold uppercase tracking-[0.12em] text-slate-700 px-3 h-10 items-center bg-slate-100/70 border-b border-slate-200" style={{ gridTemplateColumns: COLS }}>
            <div>Token details</div>
            <div>Range</div>
            <div>Currency</div>
            <div>Price</div>
            <div></div>
          </div>
          {items.length === 0 ? (
            <div className="px-3 py-6 text-center text-xs text-slate-400">No tiers yet — add the first one below</div>
          ) : (
            items.map((it) => (
              <div key={it.id} className="grid items-center gap-2 px-3 h-12 border-b border-slate-100 group" style={{ gridTemplateColumns: COLS }} data-testid={`api-cost-row-${it.id}`}>
                <Input value={it.token_details} onChange={(e) => updateRow(it.id, "token_details", e.target.value)} placeholder="e.g. Input tokens" className="h-8" />
                <Input value={it.range} onChange={(e) => updateRow(it.id, "range", e.target.value)} placeholder="e.g. 0–1M" className="h-8" />
                <select
                  value={it.currency || "USD"}
                  onChange={(e) => updateRow(it.id, "currency", e.target.value)}
                  className="h-8 px-2 rounded-md border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  data-testid={`api-cost-currency-${it.id}`}
                >
                  {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                <Input value={it.price} onChange={(e) => updateRow(it.id, "price", e.target.value)} placeholder="e.g. 5.00 / 1M" className="h-8" />
                <button
                  onClick={() => removeRow(it.id)}
                  className="opacity-0 group-hover:opacity-100 h-8 w-8 rounded-md hover:bg-rose-50 text-slate-400 hover:text-rose-600 flex items-center justify-center transition-all"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))
          )}
        </div>
        <div>
          <Button variant="outline" size="sm" onClick={addRow} data-testid="api-cost-add-row">
            <Plus className="h-3.5 w-3.5 mr-1" /> Add tier
          </Button>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} className="btn-brand" data-testid="api-cost-save">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Apis() {
  const [apis, setApis] = useState([]);
  const [apiTypes, setApiTypes] = useState([]);
  const [keyOpen, setKeyOpen] = useState(false);
  const [costOpen, setCostOpen] = useState(false);
  const [activeApi, setActiveApi] = useState(null);

  const load = async () => {
    try {
      const [a, t] = await Promise.all([api.get("/apis"), api.get("/settings/api-types")]);
      setApis(a.data);
      setApiTypes(Array.isArray(t.data) ? t.data : []);
    } catch (e) { console.error(e); }
  };
  useEffect(() => { load(); }, []);

  const updateApiTypes = async (next) => {
    setApiTypes(next); // optimistic
    try {
      const { data } = await api.put("/settings/api-types", { types: next });
      setApiTypes(data);
    } catch { toast.error("Failed to save types"); load(); }
  };

  const handleCreate = async () => {
    try {
      const { data } = await api.post("/apis", { name: "New API", status: "Free Version" });
      setApis((prev) => [...prev, data]);
      toast.success("API added");
    } catch { toast.error("Failed to add"); }
  };

  const handleUpdate = async (id, patch) => {
    try {
      const { data } = await api.patch(`/apis/${id}`, patch);
      setApis((prev) => prev.map((a) => (a.id === id ? data : a)));
      return data;
    } catch {
      toast.error("Update failed");
      throw new Error("update failed");
    }
  };

  const handleDelete = async (a) => {
    if (!window.confirm(`Delete "${a.name}"?`)) return;
    try {
      await api.delete(`/apis/${a.id}`);
      setApis((prev) => prev.filter((x) => x.id !== a.id));
      toast.success("Deleted");
    } catch { toast.error("Delete failed"); }
  };

  const handleDuplicate = async (a) => {
    try {
      await api.post(`/apis/${a.id}/duplicate`);
      await load(); // refresh so row appears right below source
      toast.success(`Duplicated "${a.name}"`);
    } catch { toast.error("Duplicate failed"); }
  };

  const openKey = (a) => { setActiveApi(a); setKeyOpen(true); };
  const openCost = (a) => { setActiveApi(a); setCostOpen(true); };

  return (
    <div className="space-y-6" data-testid="apis-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="font-display text-2xl font-semibold tracking-tight text-slate-900">APIs</h2>
          <p className="text-sm text-slate-500">Catalog of the APIs powering your agents, with keys and package costs</p>
        </div>
        <div className="flex items-center gap-2">
          <ExportImport
            filename="apis"
            headers={["id","type","name","url","description","publisher","vendor","status","key","package_costs"]}
            items={apis}
            toRow={(a) => ({
              id: a.id,
              type: a.type || "",
              name: a.name || "",
              url: a.url || "",
              description: a.description || "",
              publisher: a.publisher || "",
              vendor: a.vendor || "",
              status: a.status || "Free Version",
              key: JSON.stringify(a.key || {}),
              package_costs: JSON.stringify(a.package_costs || []),
            })}
            onImport={async (rows) => {
              let created = 0, updated = 0, skipped = 0;
              for (const r of rows) {
                let key = {};
                let package_costs = [];
                try { key = r.key ? JSON.parse(r.key) : {}; } catch { key = {}; }
                try { package_costs = r.package_costs ? JSON.parse(r.package_costs) : []; } catch { package_costs = []; }
                const body = {
                  name: r.name || "New API",
                  type: r.type || "",
                  url: r.url || "",
                  description: r.description || "",
                  publisher: r.publisher || "",
                  vendor: r.vendor || "",
                  status: r.status || "Free Version",
                  key,
                  package_costs,
                };
                try {
                  if (r.id) {
                    const existing = apis.find((x) => x.id === r.id);
                    if (existing) { await api.patch(`/apis/${r.id}`, body); updated++; continue; }
                  }
                  await api.post("/apis", body); created++;
                } catch { skipped++; }
              }
              await load();
              return { created, updated, skipped };
            }}
            testid="apis-ei"
          />
          <Button onClick={handleCreate} data-testid="new-api-button" className="btn-brand h-10 rounded-xl">
            <Plus className="h-4 w-4 mr-1" /> New API
          </Button>
        </div>
      </div>

      {apis.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center">
          <Plug className="h-10 w-10 mx-auto text-slate-300" />
          <p className="mt-3 text-sm text-slate-500">No APIs yet.</p>
          <Button onClick={handleCreate} className="btn-brand mt-4">Add first API</Button>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-slate-200">
          <div
            className="sticky top-0 z-30 grid text-[11px] font-bold uppercase tracking-[0.12em] text-slate-700 px-4 h-11 items-center bg-slate-100/95 backdrop-blur-sm border-b border-slate-200 shadow-[0_1px_0_rgba(0,0,0,0.03)] rounded-t-2xl"
            style={{ gridTemplateColumns: GRID }}
          >
            <div>Type</div>
            <div>API</div>
            <div>Description</div>
            <div>Publisher</div>
            <div>Status</div>
            <div>Vendor</div>
            <div className="text-center">Actions</div>
          </div>
          {apis.map((a) => {
            const s = statusMeta(a.status);
            const costsCount = (a.package_costs || []).length;
            const hasKey = a.key && (a.key.email || a.key.login_url || a.key.password);
            return (
              <div
                key={a.id}
                className="grid items-start gap-3 px-4 min-h-[56px] py-3 border-b border-slate-100 hover:bg-slate-50/60 transition-colors group"
                style={{ gridTemplateColumns: GRID }}
                data-testid={`api-row-${a.id}`}
              >
                {/* Type */}
                <div className="min-w-0 pt-0.5">
                  <TypeSelect
                    value={a.type || ""}
                    options={apiTypes}
                    onChange={(v) => handleUpdate(a.id, { type: v })}
                    onOptionsChange={updateApiTypes}
                    testid={`api-type-${a.id}`}
                  />
                </div>

                {/* API name + URL */}
                <div className="flex items-start gap-1 min-w-0 pt-0.5">
                  <UrlPopover
                    value={a.url}
                    onSave={(v) => handleUpdate(a.id, { url: v })}
                    testid={`api-url-${a.id}`}
                  />
                  <div className="min-w-0 flex-1">
                    <WrapInput
                      value={a.name}
                      onCommit={(v) => { if (v.trim()) handleUpdate(a.id, { name: v }); }}
                      testid={`api-name-${a.id}`}
                      className="font-medium text-slate-900"
                    />
                  </div>
                </div>

                {/* Description */}
                <div className="min-w-0 pr-3">
                  <WrapInput
                    value={a.description}
                    onCommit={(v) => handleUpdate(a.id, { description: v })}
                    testid={`api-desc-${a.id}`}
                  />
                </div>

                {/* Publisher */}
                <div className="min-w-0 pr-2">
                  <WrapInput
                    value={a.publisher}
                    onCommit={(v) => handleUpdate(a.id, { publisher: v })}
                    testid={`api-publisher-${a.id}`}
                  />
                </div>

                {/* Status */}
                <Select value={a.status} onValueChange={(v) => handleUpdate(a.id, { status: v })}>
                  <SelectTrigger
                    className={`h-8 border-none rounded-md font-medium text-xs w-full justify-between px-2 mt-0.5 ${s.bg} ${s.text} hover:brightness-95 focus:ring-0 focus:ring-offset-0`}
                    data-testid={`api-status-${a.id}`}
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUSES.map((o) => <SelectItem key={o.value} value={o.value}>{o.value}</SelectItem>)}
                  </SelectContent>
                </Select>

                {/* Vendor */}
                <div className="min-w-0 pr-2">
                  <WrapInput
                    value={a.vendor}
                    onCommit={(v) => handleUpdate(a.id, { vendor: v })}
                    testid={`api-vendor-${a.id}`}
                  />
                </div>

                {/* Actions */}
                <div className="flex items-start justify-center gap-1 pt-0.5">
                  <button
                    onClick={() => openKey(a)}
                    className={`h-8 px-2 rounded-md text-xs font-medium flex items-center gap-1 border transition-colors ${
                      hasKey ? "bg-sky-50 text-blue-700 border-sky-100 hover:border-sky-200" : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"
                    }`}
                    data-testid={`api-key-btn-${a.id}`}
                    title="Key (login & paying entity)"
                  >
                    <Key className="h-3.5 w-3.5" /> Key
                  </button>
                  <button
                    onClick={() => openCost(a)}
                    className={`h-8 px-2 rounded-md text-xs font-medium flex items-center gap-1 border transition-colors ${
                      costsCount > 0 ? "bg-mint-50 text-emerald-700 border-mint-100 hover:border-mint-200" : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"
                    }`}
                    data-testid={`api-cost-btn-${a.id}`}
                    title="Package cost tiers"
                  >
                    <DollarSign className="h-3.5 w-3.5" /> {costsCount > 0 ? costsCount : "Cost"}
                  </button>
                  <button
                    onClick={() => handleDuplicate(a)}
                    className="opacity-0 group-hover:opacity-100 h-8 w-8 rounded-md hover:bg-slate-100 text-slate-400 hover:text-slate-700 flex items-center justify-center transition-all"
                    data-testid={`api-duplicate-${a.id}`}
                    title="Duplicate"
                  >
                    <Copy className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(a)}
                    className="opacity-0 group-hover:opacity-100 h-8 w-8 rounded-md hover:bg-rose-50 text-slate-400 hover:text-rose-600 flex items-center justify-center transition-all"
                    data-testid={`api-delete-${a.id}`}
                    title="Delete"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <KeyDialog
        open={keyOpen}
        onOpenChange={setKeyOpen}
        api={activeApi}
        onSave={(patch) => handleUpdate(activeApi.id, patch)}
      />
      <PackageCostDialog
        open={costOpen}
        onOpenChange={setCostOpen}
        api={activeApi}
        onSave={(patch) => handleUpdate(activeApi.id, patch)}
      />
    </div>
  );
}
